import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MdbModalService } from 'mdb-angular-ui-kit/modal';
import { UserModel } from '../../../../models/resources/user.model';
import { PaginationMeta } from '../../../../models/pagination-meta.model';
import { AdminUsersService } from '../../../../services/admin/admin-users.service';
import {
  applyAdminPageMeta,
  buildAdminListQuery,
  moveToNextAdminPage,
  moveToPreviousAdminPage,
  moveToPreviousPageAfterDelete,
  resetAdminPagination,
} from '../admin-pagination-state';
import { CreateUserModalComponent } from '../create-user-modal/create-user-modal.component';
import { ConfirmDeleteUserModalComponent } from '../confirm-delete-user-modal/confirm-delete-user-modal.component';
import { ListPaginationBarComponent } from '../../../utils/list-pagination-bar/list-pagination-bar.component';
import { TextSearchComponent } from '../../../utils/text-search/text-search.component';
import { EnumSelectComponent } from '../../../utils/enum-select/enum-select.component';
import { MdbButtonComponent } from '../../../utils/mdb-button/mdb-button.component';
import { SpinnerComponent } from '../../../utils/spinner/spinner.component';
import { MdbCheckboxModule } from 'mdb-angular-ui-kit/checkbox';

interface OwnedMailAccountDeleteInfo {
  id: number;
  name: string | null;
}

@Component({
  selector: 'app-admin-users',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ListPaginationBarComponent,
    TextSearchComponent,
    EnumSelectComponent,
    MdbButtonComponent,
    SpinnerComponent,
    MdbCheckboxModule,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './admin-users.component.html',
})
export class AdminUsersComponent implements OnInit {
  users: UserModel[] = [];
  searchTerm = '';
  roleFilter: 'all' | 'admin' | 'user' = 'all';
  activeFilter: 'all' | 'active' | 'inactive' = 'active';
  currentPage = 1;
  lastPage = 1;
  perPage = 25;
  readonly perPageOptions = [10, 25, 50, 100];
  total = 0;
  loading = false;
  /** ID of user currently being deleted (enables spinner on that row's delete button). */
  deletingUserId: number | null = null;
  /** ID of user currently being toggled active/inactive. */
  togglingUserId: number | null = null;
  /** True while the users list is being fetched (initial load, filter, pagination). */
  listLoading = false;
  errorMessage: string | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly roleFilterOptions = { all: 'all', admin: 'admin', user: 'user' } as const;
  readonly roleFilterLabelKey = (v: string): string =>
    v === 'all' ? 'ADMIN.USERS.FILTER_ROLE_ALL' : v === 'admin' ? 'ADMIN.USERS.FILTER_ROLE_ADMIN' : 'ADMIN.USERS.FILTER_ROLE_USER';
  readonly activeFilterOptions = { all: 'all', active: 'active', inactive: 'inactive' } as const;
  readonly activeFilterLabelKey = (v: string): string =>
    v === 'all' ? 'ADMIN.USERS.FILTER_ACTIVE_ALL' : v === 'active' ? 'ADMIN.USERS.FILTER_ACTIVE_ACTIVE' : 'ADMIN.USERS.FILTER_ACTIVE_INACTIVE';

  filterForm = this.fb.group({
    roleFilter: this.fb.control<'all' | 'admin' | 'user'>('all', { nonNullable: true }),
    activeFilter: this.fb.control<'all' | 'active' | 'inactive'>('active', { nonNullable: true }),
  });

  constructor(
    private usersService: AdminUsersService,
    private modalService: MdbModalService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.filterForm.patchValue({
      roleFilter: this.roleFilter,
      activeFilter: this.activeFilter,
    });
    this.loadUsers();
  }

  loadUsers(resetPage = false): void {
    if (resetPage) {
      resetAdminPagination(this);
    }

    this.listLoading = true;
    this.usersService.getUsers(buildAdminListQuery(this))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.users = response.data;
          applyAdminPageMeta(this, response.meta, response.data.length);
          this.listLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.listLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  applyFilters(): void {
    this.roleFilter = this.filterForm.get('roleFilter')?.value ?? 'all';
    this.activeFilter = this.filterForm.get('activeFilter')?.value ?? 'active';
    this.loadUsers(true);
  }

  nextPage(): void {
    if (!moveToNextAdminPage(this)) {
      return;
    }
    this.loadUsers();
  }

  previousPage(): void {
    if (!moveToPreviousAdminPage(this)) {
      return;
    }
    this.loadUsers();
  }

  onPerPageChange(value: number): void {
    this.perPage = value;
    this.loadUsers(true);
  }

  openCreateModal(): void {
    this.openUserModal(undefined);
  }

  openEditModal(user: UserModel): void {
    this.openUserModal(user);
  }

  openDeleteConfirmModal(user: UserModel): void {
    if (this.loading) {
      return;
    }
    const modalRef = this.modalService.open(ConfirmDeleteUserModalComponent, {
      data: { userName: user.username ?? '-' },
      ignoreBackdropClick: true,
    });
    modalRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteUser(user, false);
        }
      });
  }

  private openUserModal(user?: UserModel): void {
    const modalRef = this.modalService.open(CreateUserModalComponent, {
      data: { user: user ?? null },
      ignoreBackdropClick: true,
    });

    modalRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((updated: UserModel | undefined) => {
        if (updated) {
          const index = this.users.findIndex((u) => u.id === updated.id);
          if (index >= 0) {
            this.users = this.users.map((u) => (u.id === updated.id ? updated : u));
          } else {
            this.users = [...this.users, updated];
          }
          this.cdr.markForCheck();
        }
      });
  }

  toggleActive(user: UserModel): void {
    if (this.loading) {
      return;
    }

    const userIndex = this.users.findIndex((u) => u.id === user.id);
    if (userIndex === -1) {
      return;
    }
    const originalUser = { ...this.users[userIndex] };
    const optimisticUser = { ...originalUser, active: !originalUser.active };

    this.users = this.users.map((u) => (u.id === optimisticUser.id ? optimisticUser : u));
    this.errorMessage = null;
    this.loading = true;
    this.togglingUserId = user.id;
    this.cdr.markForCheck();
    this.usersService
      .updateUser(user.id, {
        username: originalUser.username,
        email: originalUser.email ?? null,
        role: originalUser.role,
        active: optimisticUser.active,
        updatedAt: originalUser.updatedAt,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.users = this.users.map((u) => (u.id === updated.id ? updated : u));
          this.togglingUserId = null;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.users = this.users.map((u) => (u.id === originalUser.id ? originalUser : u));
          this.togglingUserId = null;
          this.loading = false;
          this.errorMessage = 'ADMIN.USERS.SAVE_FAILED';
          this.cdr.markForCheck();
        },
      });
  }

  deleteUser(user: UserModel, deleteOwnedMailAccounts = false): void {
    if (this.loading) {
      return;
    }

    this.errorMessage = null;
    this.loading = true;
    this.deletingUserId = user.id;
    this.cdr.markForCheck();
    this.usersService.deleteUser(user.id, deleteOwnedMailAccounts)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: () => {
        this.deletingUserId = null;
        this.users = this.users.filter((u) => u.id !== user.id);
        if (moveToPreviousPageAfterDelete(this, this.users.length)) {
          this.loadUsers();
          return;
        }
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error: HttpErrorResponse) => {
        if (!deleteOwnedMailAccounts && this.isOwnedMailAccountsConflict(error)) {
          this.deletingUserId = null;
          this.loading = false;
          this.cdr.markForCheck();
          this.openOwnedMailAccountsDeleteConfirmModal(user, error.error?.data?.owned_mail_accounts ?? []);
          return;
        }

        this.deletingUserId = null;
        this.loading = false;
        this.errorMessage = 'ADMIN.USERS.SAVE_FAILED';
        this.cdr.markForCheck();
      },
    });
  }

  private openOwnedMailAccountsDeleteConfirmModal(user: UserModel, ownedMailAccounts: OwnedMailAccountDeleteInfo[]): void {
    const modalRef = this.modalService.open(ConfirmDeleteUserModalComponent, {
      data: {
        userName: user.username ?? '-',
        titleKey: 'ADMIN.USERS.DELETE_CONFIRM_TITLE_WITH_MAIL_ACCOUNTS',
        messageKey: 'ADMIN.USERS.DELETE_CONFIRM_MESSAGE_WITH_MAIL_ACCOUNTS',
        submitTextKey: 'ADMIN.USERS.DELETE_WITH_MAIL_ACCOUNTS',
        showOwnedMailAccountsWarning: true,
        ownedMailAccounts,
      },
      ignoreBackdropClick: true,
    });

    modalRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteUser(user, true);
        }
      });
  }

  private isOwnedMailAccountsConflict(error: HttpErrorResponse): boolean {
    if (error.status !== 409) {
      return false;
    }

    const count = error.error?.data?.owned_mail_accounts_count;
    return typeof count === 'number' && count > 0;
  }

  /** Build PaginationMeta for list-pagination-bar from current state. */
  get paginationMeta(): PaginationMeta {
    return {
      current_page: this.currentPage,
      last_page: this.lastPage,
      per_page: this.perPage,
      total: this.total,
    };
  }
}
